import React, { useState } from 'react';
import { Eye, EyeOff, LucideIcon } from 'lucide-react';

// Kirish va ro'yxatdan o'tish sahifalari uchun umumiy qobiq.
// Telefonda karta yo'q — sahifa butun ekranni egallaydi (ilova kabi),
// sm+ ekranlarda esa markazdagi kartaga aylanadi.

interface AuthShellProps {
  title: string;
  subtitle: string;
  footer: React.ReactNode;
  children: React.ReactNode;
  /** Uzun formalar uchun: sarlavha kichikroq, forma bir ekranga sig'sin. */
  compact?: boolean;
}

export const AuthShell: React.FC<AuthShellProps> = ({ title, subtitle, footer, children, compact }) => (
  <div className="min-h-[100dvh] bg-white sm:bg-gradient-to-br sm:from-primary-50 sm:via-white sm:to-primary-100 sm:flex sm:items-center sm:justify-center sm:p-6">
    <div className="relative min-h-[100dvh] sm:min-h-0 w-full sm:max-w-md flex flex-col bg-white sm:rounded-3xl sm:shadow-xl sm:shadow-primary-900/5 overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-56 bg-gradient-to-b from-primary-50 to-white pointer-events-none"
      />

      <div className={`relative flex-1 flex flex-col px-6 pb-6 sm:px-10 sm:pt-12 ${
          compact ? 'pt-[calc(env(safe-area-inset-top)+1.25rem)]' : 'pt-[calc(env(safe-area-inset-top)+2.5rem)]'
        }`}>
        <div className={`flex flex-col items-center text-center ${compact ? 'mb-6' : 'mb-8'}`}>
          <img
            src="/logo.svg"
            alt="Amanah"
            className={`rounded-2xl shadow-lg shadow-primary-600/25 ${compact ? 'w-12 h-12 mb-4' : 'w-16 h-16 mb-5'}`}
          />
          <h1 className={`${compact ? 'text-2xl' : 'text-[1.75rem]'} leading-tight font-bold tracking-tight text-gray-900`}>
            {title}
          </h1>
          <p className="mt-2 text-[15px] text-gray-500 max-w-xs">{subtitle}</p>
        </div>

        {children}
      </div>

      <div className="relative px-6 pt-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] text-center text-[15px] text-gray-500 border-t border-gray-100 sm:border-0 sm:pb-10 sm:pt-0">
        {footer}
      </div>
    </div>
  </div>
);

interface AuthFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  icon: LucideIcon;
}

const inputClass =
  'w-full h-14 pl-12 pr-4 text-base text-gray-900 placeholder:text-gray-400 bg-gray-50 border border-gray-200 rounded-2xl ' +
  'transition focus:bg-white focus:border-primary-600 focus:ring-4 focus:ring-primary-100 focus:outline-none';

export const AuthField: React.FC<AuthFieldProps> = ({ label, icon: Icon, id, className, ...props }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-2 ml-1">
      {label}
    </label>
    <div className="relative">
      <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
      <input id={id} {...props} className={`${inputClass} ${className ?? ''}`} />
    </div>
  </div>
);

export const PasswordField: React.FC<Omit<AuthFieldProps, 'type'>> = (props) => {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <AuthField {...props} type={visible ? 'text' : 'password'} className="pr-12" />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'Parolni yashirish' : "Parolni ko'rsatish"}
        className="absolute right-1.5 bottom-1.5 w-11 h-11 flex items-center justify-center rounded-xl text-gray-400 active:bg-gray-100"
      >
        {visible ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
      </button>
    </div>
  );
};

export const AuthButton: React.FC<{ loading: boolean; loadingText: string; children: React.ReactNode }> = ({
  loading,
  loadingText,
  children,
}) => (
  <button
    type="submit"
    disabled={loading}
    className="w-full h-14 rounded-2xl bg-primary-600 hover:bg-primary-700 active:bg-primary-active text-white text-base font-semibold shadow-lg shadow-primary-600/25 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
  >
    {loading && (
      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
    )}
    {loading ? loadingText : children}
  </button>
);

export const AuthAlert: React.FC<{ tone: 'error' | 'warning'; icon?: LucideIcon; title?: string; children?: React.ReactNode }> = ({
  tone,
  icon: Icon,
  title,
  children,
}) => (
  <div
    role="alert"
    className={`flex items-start gap-3 px-4 py-3 rounded-2xl mb-6 text-[15px] ${
      tone === 'error' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-800'
    }`}
  >
    {Icon && <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />}
    <div>
      {title && <p className="font-semibold">{title}</p>}
      {children && <div className={title ? 'text-sm mt-0.5' : ''}>{children}</div>}
    </div>
  </div>
);
